import { addDays, format, parseISO } from 'date-fns';
import { cropText, formatNumber, formatPrice } from '../../utils/util';

export const CHART_COLORS = [
  '#3E9BE4',
  '#EF4444',
  '#2DD4BF',
  '#FACC15',
  '#06B6D4',
  '#3B82F6',
  '#10B981',
  '#F59E0B',
  '#8B5CF6',
  '#EC4899',
  '#84CC16',
  '#F97316',
  '#6366F1',
  '#14B8A6',
  '#F87171',
  '#A855F7',
  '#22C55E',
  '#FB923C',
  '#F43F5E',
  '#0EA5E9',
  '#A78BFA',
  '#65A30D',
  '#60A5FA',
  '#C084FC',
  '#FBBF24',
  '#818CF8',
  '#34D399',
  '#E879F9',
  '#94A3B8',
  '#38BDF8',
  '#FB7185',
];

export const mapIdsToLabels = (ids = [], labelMap = {}) => ids.map(id => labelMap[id] || cropText(id, 12));

export const sumArrays = arrays => {
  const maxLength = arrays.reduce((max, current) => Math.max(max, current?.length || 0), 0);
  return Array.from({ length: maxLength }, (_, index) =>
    arrays.reduce((sum, current) => sum + Number(current?.[index] || 0), 0),
  );
};

export const formatIsoDates = dates =>
  dates.map(date => {
    try {
      return format(parseISO(date), 'd MMM');
    } catch (err) {
      return date;
    }
  });

export const calculateTimeUnit = (from, to) => {
  if (!from || !to) {
    return 'day';
  }

  const fromDate = new Date(from);
  const toDate = new Date(to);
  const diffMs = toDate - fromDate;
  const diffDays = diffMs / (1000 * 60 * 60 * 24);

  if (diffDays < 2) {
    return 'hour';
  }

  if (diffDays < 40) {
    return 'day';
  }

  if (diffDays < 60) {
    return 'week';
  }

  return 'month';
};

export const formatTimeCategoriesByRange = (dates = [], range) => {
  const timeUnit = calculateTimeUnit(range?.[0], range?.[1]);

  return dates.map(date => {
    try {
      const parsedDate = parseISO(date);

      if (timeUnit === 'hour') {
        return format(parsedDate, 'd MMM HH:mm');
      }

      if (timeUnit === 'week') {
        return `Week of ${format(parsedDate, 'd MMM')}`;
      }

      if (timeUnit === 'month') {
        return format(parsedDate, 'MMM yyyy');
      }

      return format(parsedDate, 'd MMM');
    } catch (err) {
      return date;
    }
  });
};

export const buildCategoriesFromRange = (range, pointsLength) => {
  if (!range?.[0] || !pointsLength) {
    return [];
  }

  const startDate = parseISO(range[0]);
  return Array.from({ length: pointsLength }, (_, index) => format(addDays(startDate, index), 'd MMM'));
};

export const formatValueByType = (value, type = 'number') => {
  if (type === 'currency') {
    return `$${formatPrice(Number(value || 0))}`;
  }

  if (type === 'percent') {
    return `${Number(value || 0).toFixed(2)}%`;
  }

  if (type === 'duration') {
    const totalSeconds = Number(value || 0);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);

    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  }

  return formatNumber(Number(value || 0));
};

export const sortTooltipPointsDescending = (points = []) =>
  [...points].sort((firstPoint, secondPoint) => Number(secondPoint?.y || 0) - Number(firstPoint?.y || 0));

export const getDefaultTooltipFormatter = valueType =>
  function () {
    const points = sortTooltipPointsDescending(this.points || [this.point]);
    let tooltipText = `<b>${this.point.category}</b><br/><br/>`;

    points.forEach(point => {
      const markerStyle = `<span style="color:${point.color}">\u25CF</span>`;
      tooltipText += `${markerStyle} ${point.series.name}: ${formatValueByType(point.y, valueType)}<br/>`;
    });

    return tooltipText;
  };

export const getDefaultTooltipFormatterWithTotal = valueType =>
  function () {
    const points = sortTooltipPointsDescending(this.points || [this.point]);
    const total = points.reduce((sum, point) => sum + Number(point?.y || 0), 0);
    let tooltipText = `<b>${this.point.category}</b><br/><br/>`;

    points.forEach(point => {
      const markerStyle = `<span style="color:${point.color}">\u25CF</span>`;
      tooltipText += `${markerStyle} ${point.series.name}: ${formatValueByType(point.y, valueType)}<br/>`;
    });

    tooltipText += `<br/><b>Total: ${formatValueByType(total, valueType)}</b>`;

    return tooltipText;
  };

export const getDurationWithMessagesTooltipFormatter = () =>
  function () {
    const points = sortTooltipPointsDescending(this.points || [this.point]);
    let tooltipText = `<b>${this.point.category}</b><br/><br/>`;

    points.forEach(point => {
      const markerStyle = `<span style="color:${point.color}">\u25CF</span>`;
      const pointIndex = point.point.index;
      const messages = point.series.userOptions.messages?.[pointIndex];

      tooltipText += `${markerStyle} <b>${point.series.name}:</b> ${formatValueByType(point.y, 'duration')}<br/>`;
      tooltipText += `<span style="color:transparent">\u25CF</span> Messages: ${formatValueByType(messages, 'number')}<br/>`;
    });

    return tooltipText;
  };

export const getPpvRateWithCountsTooltipFormatter = () =>
  function () {
    const points = sortTooltipPointsDescending(this.points || [this.point]);
    let tooltipText = `<b>${this.point.category}</b><br/><br/>`;

    points.forEach(point => {
      const markerStyle = `<span style="color:${point.color}">\u25CF</span>`;
      const pointIndex = point.point.index;
      const offers = point.series.userOptions.offerCount?.[pointIndex];
      const purchases = point.series.userOptions.purchaseCount?.[pointIndex];

      tooltipText += `${markerStyle} <b>${point.series.name}:</b> ${formatValueByType(point.y, 'percent')}<br/>`;
      tooltipText += `<span style="color:transparent">\u25CF</span> Offers: ${formatValueByType(offers, 'number')}<br/>`;
      tooltipText += `<span style="color:transparent">\u25CF</span> Purchases: ${formatValueByType(purchases, 'number')}<br/>`;
    });

    return tooltipText;
  };

export const getConversionRateWithSpendersTooltipFormatter = () =>
  function () {
    const points = sortTooltipPointsDescending(this.points || [this.point]);
    let tooltipText = `<b>${this.point.category}</b><br/><br/>`;

    points.forEach(point => {
      const markerStyle = `<span style="color:${point.color}">●</span>`;
      const pointIndex = point.point.index;
      const spenders = point.series.userOptions.spenders?.[pointIndex];
      const nonSpenders = point.series.userOptions.nonSpenders?.[pointIndex];
      const total = Number(spenders || 0) + Number(nonSpenders || 0);

      tooltipText += `${markerStyle} <b>${point.series.name}:</b> ${formatValueByType(point.y, 'percent')}<br/>`;
      tooltipText += `<span style="color:transparent">●</span> Spenders: ${formatValueByType(spenders, 'number')}<br/>`;
      tooltipText += `<span style="color:transparent">●</span> Non-Spenders: ${formatValueByType(nonSpenders, 'number')}<br/>`;
      tooltipText += `<span style="color:transparent">●</span> Total: ${formatValueByType(total, 'number')}<br/>`;
    });

    return tooltipText;
  };

export const getAiSuggestionsUsageWithTotalMessagesTooltipFormatter = () =>
  function () {
    const points = sortTooltipPointsDescending(this.points || [this.point]);
    let tooltipText = `<b>${this.point.category}</b><br/><br/>`;

    points.forEach(point => {
      const markerStyle = `<span style="color:${point.color}">\u25CF</span>`;
      const pointIndex = point.point.index;
      const totalMessages = point.series.userOptions.totalMessages?.[pointIndex];

      tooltipText += `${markerStyle} <b>${point.series.name}:</b> ${formatValueByType(point.y, 'percent')}<br/>`;
      tooltipText += `<span style="color:transparent">\u25CF</span> Total Messages: ${formatValueByType(totalMessages, 'number')}<br/>`;
    });

    return tooltipText;
  };
